/**
 * ParticleBurst
 * ─────────────
 * Explosion effect on CTA clicks.
 * Creates particles that burst from click point.
 */

import { useRef, useCallback } from 'react';
import gsap from 'gsap';
import { prefersReducedMotion } from './tokens';

// Particle pool for reuse
const particlePool = [];
const POOL_SIZE = 50;

// Create particle element
const createParticle = () => {
  const particle = document.createElement('div');
  particle.style.cssText = `
    position: fixed;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    pointer-events: none;
    z-index: 10000;
    will-change: transform, opacity;
  `;
  return particle;
};

// Initialize pool
if (typeof document !== 'undefined') {
  for (let i = 0; i < POOL_SIZE; i++) {
    particlePool.push(createParticle());
  }
}

export function useParticleBurst(options = {}) {
  const {
    count = 20,
    colors = ['#00C8DC', '#00A0B0', '#0088CC', '#006699'],
    radius = 100,
    duration = 0.6,
  } = options;
  
  const activeParticles = useRef([]);
  
  const burst = useCallback((x, y) => {
    if (prefersReducedMotion()) return;
    
    const particles = [];
    
    for (let i = 0; i < count; i++) {
      // Get particle from pool or create new
      let particle = particlePool.pop();
      if (!particle) {
        particle = createParticle();
      }
      
      // Random properties
      const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.5;
      const distance = radius * (0.5 + Math.random() * 0.5);
      const size = 4 + Math.random() * 6;
      const color = colors[Math.floor(Math.random() * colors.length)];
      
      // Initial position
      particle.style.left = `${x}px`;
      particle.style.top = `${y}px`;
      particle.style.width = `${size}px`;
      particle.style.height = `${size}px`;
      particle.style.backgroundColor = color;
      particle.style.opacity = '1';
      particle.style.transform = 'translate(-50%, -50%) scale(1)';
      
      document.body.appendChild(particle);
      particles.push(particle);
      
      // Animate
      gsap.to(particle, {
        x: Math.cos(angle) * distance,
        y: Math.sin(angle) * distance - 30, // Slight upward bias
        scale: 0,
        opacity: 0,
        duration: duration + Math.random() * 0.2,
        ease: "power2.out",
        onComplete: () => {
          if (particle.parentNode) {
            particle.parentNode.removeChild(particle);
          }
          particle.style.transform = '';
          particlePool.push(particle);
        },
      });
    }
    
    activeParticles.current = particles;
  }, [count, colors, radius, duration]);
  
  return burst;
}

// HOC for buttons with particle burst
export function withParticleBurst(WrappedComponent) {
  return function ParticleBurstWrapper(props) {
    const burst = useParticleBurst();
    
    const handleClick = (e) => {
      burst(e.clientX, e.clientY);
      props.onClick?.(e);
    };
    
    return <WrappedComponent {...props} onClick={handleClick} />;
  };
}

export default useParticleBurst;
